/**
 * Activity Service
 *
 * Handles CRUD and lifecycle operations for Activity instances.
 */

import { apiClient } from '@/shared/services/api';
import {
  Activity,
  ActivityFormData,
  ActivityFilters,
  ActivityEnrollment,
  ActivityRoleAssignment,
  ActivityDeleteResult,
  RoleAssignmentInput,
  DeleteMode,
} from '../types';

// ── Activities ────────────────────────────────────────────────────────────────

function buildActivityParams(filters?: ActivityFilters): Record<string, string> {
  const params: Record<string, string> = {};
  if (filters?.templateId) params.template = filters.templateId;
  if (filters?.status) params.status = filters.status;
  if (filters?.dateFrom) params.date_from = filters.dateFrom;
  if (filters?.dateTo) params.date_to = filters.dateTo;
  return params;
}

export async function fetchActivities(filters?: ActivityFilters): Promise<Activity[]> {
  const response = await apiClient.get<{ results: Activity[]; count: number } | Activity[]>(
    '/api/v1/activities/',
    { params: buildActivityParams(filters) }
  );
  const data = response.data;
  return Array.isArray(data) ? data : data.results;
}

/** Backend cap — `StandardPagination.max_page_size` on shaka-api. */
const ACTIVITIES_MAX_PAGE_SIZE = 200;
/** Safety valve: 40 pages × 200 = 8 000 sessions, far past any real season. */
const ACTIVITIES_MAX_PAGES = 40;

/**
 * Every activity matching the filters, walking DRF pagination to the end.
 *
 * `fetchActivities` returns a single page (50 rows), which silently truncates
 * anything that reasons over a whole recurrence series — a season-long weekly
 * rule generates well past one page.
 */
export async function fetchAllActivities(filters?: ActivityFilters): Promise<Activity[]> {
  const params = { ...buildActivityParams(filters), page_size: String(ACTIVITIES_MAX_PAGE_SIZE) };
  const all: Activity[] = [];

  for (let page = 1; page <= ACTIVITIES_MAX_PAGES; page += 1) {
    const response = await apiClient.get<
      { results: Activity[]; count: number; next: string | null } | Activity[]
    >('/api/v1/activities/', { params: { ...params, page: String(page) } });
    const data = response.data;
    if (Array.isArray(data)) {
      all.push(...data);
      break;
    }
    all.push(...data.results);
    if (!data.next) break;
  }

  return all;
}

export async function fetchActivity(id: string): Promise<Activity> {
  const response = await apiClient.get<Activity>(`/api/v1/activities/${id}/`);
  return response.data;
}

export async function createActivity(data: ActivityFormData): Promise<Activity> {
  const payload: Record<string, unknown> = {
    location: data.locationId,
    template: data.templateId,
    date: data.date,
    start_time: data.startTime,
    end_time: data.endTime,
  };
  if (data.priceOverride !== undefined) payload.price_override = data.priceOverride;
  if (data.maxParticipantsOverride !== undefined) payload.max_participants_override = data.maxParticipantsOverride;
  if (data.allowWalkInsOverride !== undefined) payload.allow_walk_ins_override = data.allowWalkInsOverride;
  if (data.enrollmentCutoffMinutesOverride !== undefined) payload.enrollment_cutoff_minutes_override = data.enrollmentCutoffMinutesOverride;
  if (data.staffPriceOverride !== undefined) payload.staff_price_override = data.staffPriceOverride;
  if (data.internalResponsible !== undefined) payload.internal_responsible = data.internalResponsible;
  if (data.externalProviderName !== undefined) payload.external_provider_name = data.externalProviderName;
  if (data.externalCostOverride !== undefined) payload.external_cost_override = data.externalCostOverride;
  if (data.notes !== undefined) payload.notes = data.notes;

  const response = await apiClient.post<Activity>('/api/v1/activities/', payload);
  return response.data;
}

export async function updateActivity(
  id: string,
  data: Partial<ActivityFormData>,
  options?: { editMode?: 'single' | 'this_and_future' | 'all' }
): Promise<Activity> {
  const payload: Record<string, unknown> = {};
  if (options?.editMode && options.editMode !== 'single') payload.edit_mode = options.editMode;
  if (data.templateId !== undefined) payload.template = data.templateId;
  if (data.date !== undefined) payload.date = data.date;
  if (data.startTime !== undefined) payload.start_time = data.startTime;
  if (data.endTime !== undefined) payload.end_time = data.endTime;
  if (data.priceOverride !== undefined) payload.price_override = data.priceOverride;
  if (data.maxParticipantsOverride !== undefined) payload.max_participants_override = data.maxParticipantsOverride;
  if (data.allowWalkInsOverride !== undefined) payload.allow_walk_ins_override = data.allowWalkInsOverride;
  if (data.enrollmentCutoffMinutesOverride !== undefined) payload.enrollment_cutoff_minutes_override = data.enrollmentCutoffMinutesOverride;
  if (data.staffPriceOverride !== undefined) payload.staff_price_override = data.staffPriceOverride;
  if (data.internalResponsible !== undefined) payload.internal_responsible = data.internalResponsible;
  if (data.externalProviderName !== undefined) payload.external_provider_name = data.externalProviderName;
  if (data.externalCostOverride !== undefined) payload.external_cost_override = data.externalCostOverride;
  if (data.notes !== undefined) payload.notes = data.notes;

  const response = await apiClient.patch<Activity>(`/api/v1/activities/${id}/`, payload);
  return response.data;
}

/** Copy this activity's staff roster onto its series (replace semantics). */
export async function propagateRoleAssignments(
  id: string,
  editMode: 'this_and_future' | 'all',
): Promise<{ updated: number; created: number; deleted: number; completedSkipped: number }> {
  const res = await apiClient.post<{ updated: number; created: number; deleted: number; completedSkipped: number }>(
    `/api/v1/activities/${id}/propagate-assignments/`,
    { editMode },
  );
  return res.data;
}

export async function deleteActivity(
  id: string,
  deleteMode: DeleteMode = 'single',
  cancelEnrollments: boolean = true,
): Promise<ActivityDeleteResult> {
  // apiClient uses the fetch API — RequestConfig extends RequestInit, so the
  // body-on-DELETE payload goes through the standard `body` field (serialized
  // as JSON like any other unsafe method), not axios-style `data`.
  const response = await apiClient.delete<ActivityDeleteResult>(`/api/v1/activities/${id}/`, {
    body: JSON.stringify({ delete_mode: deleteMode, cancel_enrollments: cancelEnrollments }),
  });
  return response.data;
}

export async function completeActivity(id: string): Promise<Activity> {
  const response = await apiClient.post<Activity>(`/api/v1/activities/${id}/complete/`);
  return response.data;
}

/**
 * Cancelar una sesión.
 *
 * Cuando la sede exige autorización para cancelar algo ya empezado
 * (`activityCancelAfterStartRequiresManager`, por defecto ON), el backend
 * contesta 409 `activity_cancel_authorization_required` si no vienen
 * credenciales. La contraseña es auto-aprobación —se comprueba contra TU
 * cuenta— y la pulsera es la única vía para que autorice otra persona.
 */
export async function cancelActivity(
  id: string,
  reason?: string,
  auth?: { approverPassword?: string; approverNfcUid?: string },
): Promise<Activity> {
  const payload: Record<string, string> = {};
  if (reason) payload.reason = reason;
  if (auth?.approverPassword) payload.approverPassword = auth.approverPassword;
  if (auth?.approverNfcUid) payload.approverNfcUid = auth.approverNfcUid;
  const response = await apiClient.post<Activity>(`/api/v1/activities/${id}/cancel/`, payload);
  return response.data;
}

export async function fetchActivityEnrollments(activityId: string): Promise<ActivityEnrollment[]> {
  const response = await apiClient.get<ActivityEnrollment[] | { results: ActivityEnrollment[] }>(
    `/api/v1/activities/${activityId}/enrollments/`
  );
  const data = response.data;
  return Array.isArray(data) ? data : data.results;
}

// ── Role assignments (staff in charge / helpers) ────────────────────────────────

export async function fetchRoleAssignments(activityId: string): Promise<ActivityRoleAssignment[]> {
  const response = await apiClient.get<ActivityRoleAssignment[] | { results: ActivityRoleAssignment[] }>(
    '/api/v1/activity-role-assignments/',
    { params: { activity: activityId } }
  );
  const data = response.data;
  return Array.isArray(data) ? data : data.results;
}

export async function createRoleAssignment(
  activityId: string,
  input: RoleAssignmentInput,
): Promise<ActivityRoleAssignment> {
  const response = await apiClient.post<ActivityRoleAssignment>(
    '/api/v1/activity-role-assignments/',
    {
      activity: activityId,
      template_role: input.templateRoleId,
      staff_member: input.staffMemberId,
    },
  );
  return response.data;
}

export async function deleteRoleAssignment(id: string): Promise<void> {
  await apiClient.delete(`/api/v1/activity-role-assignments/${id}/`);
}
